import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Custom tooltip component for consistent styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium text-foreground mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Promise Score Trend Chart
export const ScoreTrendChart = ({ data, height = 200 }) => {
  // Transform data for chart
  const chartData = data.map(d => ({
    date: d.bucket_start ? new Date(d.bucket_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
    score: d.score || 0,
    target: 90 // Target score line
  }));
  
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
        <XAxis 
          dataKey="date" 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
        />
        <YAxis 
          domain={[0, 100]} 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Line 
          type="monotone" 
          dataKey="target" 
          stroke="hsl(var(--muted-foreground))" 
          strokeDasharray="5 5"
          strokeWidth={1}
          dot={false}
          name="Target"
        />
        <Line 
          type="monotone" 
          dataKey="score" 
          stroke="#3EF1C7" 
          strokeWidth={2}
          dot={{ fill: '#3EF1C7', r: 4 }}
          activeDot={{ r: 6 }}
          name="Score"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Disappointment Trend Chart
export const DisappointmentTrendChart = ({ data, height = 200 }) => {
  const chartData = data.map(d => ({
    date: d.bucket_start ? new Date(d.bucket_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
    high: d.high || 0,
    med: d.med || 0,
    low: d.low || 0,
    total: d.count || 0
  }));
  
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
        <XAxis 
          dataKey="date" 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
        />
        <YAxis 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="high" stackId="a" fill="#ef4444" name="High" radius={[0, 0, 0, 0]} />
        <Bar dataKey="med" stackId="a" fill="#eab308" name="Medium" radius={[0, 0, 0, 0]} />
        <Bar dataKey="low" stackId="a" fill="#22c55e" name="Low" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Driver Distribution Chart
export const DriverDistributionChart = ({ data, height = 200 }) => {
  const chartData = data.map(d => ({
    type: d.type?.replace(/_/g, ' ') || 'Unknown',
    count: d.count || 0,
    weighted: d.weighted || 0
  }));
  
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
        <XAxis 
          type="number" 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
        />
        <YAxis 
          type="category" 
          dataKey="type" 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={75}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar 
          dataKey="count" 
          fill="#3EF1C7" 
          radius={[0, 4, 4, 0]}
          name="Events"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Benchmark Comparison Chart
export const BenchmarkComparisonChart = ({ brandScore, benchmark, height = 150 }) => {
  if (!benchmark) return null;
  
  const chartData = [
    { name: 'P25', value: benchmark.p25 || 0, fill: '#6b7280' },
    { name: 'P50', value: benchmark.p50 || 0, fill: '#eab308' },
    { name: 'P75', value: benchmark.p75 || 0, fill: '#22c55e' },
    { name: 'You', value: brandScore || 0, fill: '#3EF1C7' }
  ];
  
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
        <XAxis 
          dataKey="name" 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
        />
        <YAxis 
          domain={[0, 100]} 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar 
          dataKey="value" 
          radius={[4, 4, 0, 0]}
          name="Score"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Revenue Impact Chart
export const RevenueImpactChart = ({ data, height = 200 }) => {
  if (!data) return null;
  
  const costs = data.estimated_costs || {};
  const chartData = [
    { name: 'Cancellations', value: costs.margin_loss_cancellations || 0, fill: '#ef4444' },
    { name: 'Backorders', value: costs.margin_loss_backorders || 0, fill: '#f97316' },
    { name: 'Promise Breaks', value: costs.margin_loss_promise_break || 0, fill: '#eab308' },
    { name: 'Late/Partial', value: costs.margin_loss_late_partial || 0, fill: '#84cc16' },
    { name: 'Support', value: costs.support_cost || 0, fill: '#06b6d4' }
  ].filter(d => d.value > 0);
  
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
        <XAxis 
          type="number" 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
          tickFormatter={(v) => `$${v.toFixed(0)}`}
        />
        <YAxis 
          type="category" 
          dataKey="name" 
          stroke="hsl(var(--muted-foreground))" 
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={85}
        />
        <Tooltip 
          formatter={(value) => [`$${value.toFixed(2)}`, 'Cost']}
          content={<CustomTooltip />}
        />
        <Bar 
          dataKey="value" 
          radius={[0, 4, 4, 0]}
          name="Cost"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default {
  ScoreTrendChart,
  DisappointmentTrendChart,
  DriverDistributionChart,
  BenchmarkComparisonChart,
  RevenueImpactChart
};
